'use client';

import React, { useState, useEffect } from 'react';
import { X, Trash } from '@phosphor-icons/react';
import { useCart } from 'stores/cartPOSContext';
import { CartStatus, CartItem } from 'types/common';
import { useCartSession } from 'stores/cartSessionContext';
import { formatPrice, getFilePath } from 'utils';
import { logo_with_name } from 'utils/importFile';
import SelectCommon from '../Select/SelectCommon';
import { InputCommon } from '../Input/InputCommon';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';

interface SpecItem {
  id: string;
  dimensions: string[];
  quantity: number;
}

export const CartItemCard: React.FC<{ item: CartItem; isSession?: boolean }> = ({ item, isSession = false }) => {
  const { updateItem, removeItem, getActiveCart } = useCart();
  const {
    updateItem: updateItemSession,
    removeItem: removeItemSession,
    getActiveCart: getActiveCartSession,
  } = useCartSession();
  const location = useLocation();
  const initialStep = location.state?.initialStep || 0;

  const activeCart = isSession ? getActiveCartSession() : getActiveCart();
  const [selectedUnit, setSelectedUnit] = useState<string>(item.unit?.id || '');
  const [specs, setSpecs] = useState<SpecItem[]>([]);
  const [editableName, setEditableName] = useState<string>(item.name || '');
  const [manualQuantity, setManualQuantity] = useState<boolean>(false);
  const [selectedAttribute, setSelectedAttribute] = useState<string | null>(item.attribute?.id || null);
  const isBetween1280and1350 = useMediaQuery({ minWidth: 1280, maxWidth: 1349 });
  const [isCompleted, setIsCompleted] = useState(false);
  useEffect(() => {
    if (activeCart?.status === CartStatus.COMPLETED || initialStep === 2) {
      setIsCompleted(true);
    } else {
      setIsCompleted(false);
    }
  }, [activeCart, initialStep]);
  useEffect(() => {
    if (item.attribute?.id) {
      setSelectedAttribute(item.attribute.id);
    }
  }, [item.attribute]);
  useEffect(() => {
    if (item.productVersion.category?.saleDimensions) {
      if (specs.length === 0) {
        const dimensions = item.productVersion.category.saleDimensions.split('|');
        if (dimensions.length > 0) {
          setSpecs([
            {
              id: `spec-${Date.now()}`,
              dimensions: Array(dimensions.length).fill(''),
              quantity: 0,
            },
          ]);
        }
      }
      if (item.saleItemDimesions?.length) {
        try {
          const savedSpecs = item.saleItemDimesions.map((dim) => ({
            id: dim.id || `spec-${Date.now()}-${Math.random()}`,
            dimensions: dim.dimensions.split('|'),
            quantity: dim.quantity,
          }));
          setSpecs(savedSpecs);
        } catch (error) {
          console.error('Error parsing saved dimensions', error);
        }
      }
    }
  }, [item.productVersion.category?.saleDimensions]);

  useEffect(() => {
    if (!manualQuantity && specs.length > 0) {
      const totalSpecsQuantity = calculateTotalSpecsQuantity();
      if (totalSpecsQuantity !== item.quantity) {
        if (totalSpecsQuantity > 0) {
          const updateFn = isSession ? updateItemSession : updateItem;
          updateFn(item.id, { quantity: totalSpecsQuantity });
        }
        const saleItemDimesions = specs.map((spec, index) => ({
          id: spec.id,
          saleOrderItemId: item.id,
          dimensions: spec.dimensions.join('|'),
          quantity: spec.quantity,
          order: index,
        }));
        const updateFn = isSession ? updateItemSession : updateItem;
        updateFn(item.id, { saleItemDimesions });
      }
    }
  }, [specs]);

  const availableUnits = [
    item.productVersion.baseUnit,
    ...(item.productVersion.productUnits?.map((pu) => pu.unit) || []),
  ].filter(Boolean);

  const availableAttributes = item.productVersion.productAttributes || [];

  const calculateTotalSpecsQuantity = () => {
    return specs.reduce((sum, spec) => {
      const dimensionValues = spec.dimensions.map((d) => parseFloat(d) || 0);
      const nonZeroDimensions = dimensionValues.filter((d) => d > 0);
      if (nonZeroDimensions.length > 0) {
        const specQuantity = nonZeroDimensions.reduce((product, val) => product * val, 1);
        return Number((sum + specQuantity).toFixed(2));
      }
      return Number(sum.toFixed(2));
    }, 0);
  };

  const calculateSpecQuantity = (dimensions: string[]) => {
    const dimensionValues = dimensions.map((d) => parseFloat(d) || 0);
    const nonZeroDimensions = dimensionValues.filter((d) => d > 0);
    if (nonZeroDimensions.length > 0) {
      return Number(nonZeroDimensions.reduce((product, val) => product * val, 1).toFixed(2));
    }
    return 0;
  };

  const handleUnitChange = (unitId: string) => {
    setSelectedUnit(unitId);
    const selectedProductUnit = item.productVersion.productUnits?.find((pu) => pu.unit?.id === unitId);
    const updateFn = isSession ? updateItemSession : updateItem;
    updateFn(item.id, {
      unit: selectedProductUnit?.unit || item.productVersion.baseUnit,
      unitConvertValue: selectedProductUnit?.convertValue || 1,
    });
  };

  const handleAttributeChange = (attributeId: string) => {
    setSelectedAttribute(attributeId);
    const selectedAttr = availableAttributes.find((attr) => attr.id === attributeId);
    const updateFn = isSession ? updateItemSession : updateItem;
    updateFn(item.id, {
      attribute: selectedAttr,
      unitPrice: Number(item.productVersion.salePrice || 0) + Number(selectedAttr?.salePrice || 0),
    });
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue === '') {
      setManualQuantity(true);
      const updateFn = isSession ? updateItemSession : updateItem;
      updateFn(item.id, { quantity: 0 });
      return;
    }

    const value = Number(parseFloat(inputValue).toFixed(2));
    if (!isNaN(value)) {
      setManualQuantity(true);
      const updateFn = isSession ? updateItemSession : updateItem;
      updateFn(item.id, { quantity: value });
    }
  };
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue === '') {
      const updateFn = isSession ? updateItemSession : updateItem;
      updateFn(item.id, { unitPrice: 0 });
      return;
    }

    const value = parseFloat(inputValue);
    if (!isNaN(value)) {
      const updateFn = isSession ? updateItemSession : updateItem;
      updateFn(item.id, { unitPrice: value });
    }
  };
  const handleRemoveItem = () => {
    const removeFn = isSession ? removeItemSession : removeItem;
    removeFn(item.id);
  };

  const handleSpecDimensionChange = (specId: string, dimIndex: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const newSpecs = specs.map((spec) => {
      if (spec.id === specId) {
        const newDimensions = [...spec.dimensions];
        newDimensions[dimIndex] = value;
        const newQuantity = calculateSpecQuantity(newDimensions);
        return {
          ...spec,
          dimensions: newDimensions,
          quantity: newQuantity,
        };
      }
      return spec;
    });
    setSpecs(newSpecs);
    setManualQuantity(false);
  };

  const handleAddSpec = () => {
    if (item.productVersion.category?.saleDimensions) {
      const dimensions = item.productVersion.category.saleDimensions.split('|');
      const newSpec = {
        id: `spec-${Date.now()}-${Math.random()}`,
        dimensions: Array(dimensions.length).fill(''),
        quantity: 0,
      };
      setSpecs([...specs, newSpec]);
      setManualQuantity(false);
    }
  };

  const handleRemoveSpec = (specId: string) => {
    const newSpecs = specs.filter((spec) => spec.id !== specId);
    setSpecs(newSpecs);
    setManualQuantity(false);
  };
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setEditableName(newName);

    // Update the item in the cart
    const updateFn = isSession ? updateItemSession : updateItem;
    updateFn(item.id, { name: newName });
  };
  const totalPrice = (item.unitPrice || 0) * (item.quantity || 0);

  const dimensionLabels = item.productVersion.category?.saleDimensions
    ? item.productVersion.category.saleDimensions.split('|').filter((item) => item.trim() !== '')
    : [];

  return (
    <div className='p-3 bg-[var(--textBlack4)] rounded-lg flex flex-col gap-3'>
      <div className='grid grid-cols-[auto_minmax(0,1fr)_auto_auto]  gap-3 items-start'>
        {/* Product Image */}
        <div className='w-16 h-14 row-span-2'>
          <img
            src={
              item.productVersion.productImages?.[0]?.image?.filePath
                ? `${getFilePath()}/${item.productVersion.productImages[0].image.filePath}`
                : logo_with_name
            }
            alt={item.productVersion.name}
            className='h-full w-full rounded-lg object-cover'
          />
        </div>

        <div className='overflow-hidden flex items-start gap-[8px] justify-between'>
          <div className='flex flex-1 flex-wrap'>
            {' '}
            {!isSession ? (
              <InputCommon
                className='font-semibold text-sm !h-[32px] '
                value={editableName}
                onChange={handleNameChange}
                placeholder='Tên sản phẩm'
                disabled={isCompleted}
                variantStatus='default'
                hardSize='md'
              />
            ) : (
              <span className='text-black text-base font-bold'>
                {item.productVersion.name} {item.productVersion.color} {item.productVersion.dimension}{' '}
              </span>
            )}
            <span className='text-black text-xs'>- {item.productVersion.product?.code || ''} </span>
          </div>

          <div className='text-black/60 text-sm font-medium'>Đơn giá:</div>
        </div>

        <div className='flex items-center gap-2 justify-end'>
          <InputCommon
            className='!max-w-[100px] h-6 rounded-lg text-center'
            placeholder='Đơn giá'
            value={item.unitPrice || ''}
            onChange={handlePriceChange}
            type='money'
            min={0}
            variantStatus='default'
            radius='lg'
            disabled={isCompleted}
            hardSize='xs'
          />
        </div>

        <button
          onClick={handleRemoveItem}
          className={
            'w-3.5 h-3.5 flex items-center justify-center ' + (isCompleted ? ' opacity-50 cursor-not-allowed' : '')
          }
          aria-label='Xóa sản phẩm'
          disabled={isCompleted}
        >
          <Trash size={14} className='text-black/80' />
        </button>
        <div className='col-span-1 col-start-2 grid grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-2.5'>
          <div className='inline-flex justify-start items-start gap-1'>
            <span className='text-[var(--textBalck56)] text-xs font-normal'>TH:</span>
            <span className='text-[var(--primaryColor500)] text-xs font-normal'>
              {item.productVersion.brand?.name || '---'}
            </span>
          </div>
          <div className='inline-flex justify-start items-start gap-1'>
            <span className='text-[var(--textBalck56)] text-xs font-normal'>DM:</span>
            <span className='text-[var(--primaryColor500)] text-xs font-normal'>
              {item.productVersion.category?.name || '---'}
            </span>
          </div>
        </div>
        <div className='col-span-1 col-start-2 grid grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-2.5'>
          <SelectCommon
            className='h-6 p-0 flex items-center'
            disabled={isCompleted}
            value={selectedUnit}
            onChange={handleUnitChange}
            options={availableUnits.map((unit) => ({
              label: unit?.name || '',
              value: unit?.id || '',
            }))}
            sizeSelect='sm'
            variant='filled'
            statusSelect='default'
            bg='default'
            widthSelect='100%'
            style={{
              borderRadius: '0.5rem',
              height: '24px',
            }}
          />

          <SelectCommon
            className='h-6 min-w-[100px] p-0 flex items-center'
            disabled={isCompleted}
            placeholder={availableAttributes.length > 0 ? 'Chọn thuộc tính' : 'Không có thuộc tính'}
            value={selectedAttribute || undefined}
            onChange={handleAttributeChange}
            options={availableAttributes.map((attr) => ({
              label: (
                <span>
                  <span>{attr?.attribute?.name || ''}</span>
                  {attr.salePrice > 0 && (
                    <span className='text-[var(--sematicSuccessColor)] ml-1'>+{formatPrice(attr.salePrice)}</span>
                  )}
                </span>
              ),
              value: attr.id,
            }))}
            sizeSelect='sm'
            variant='filled'
            statusSelect='default'
            bg='default'
            allowClear
            style={{
              borderRadius: '0.5rem',
              height: '24px',
            }}
          />
        </div>

        <div className='col-span-1 col-start-3 flex justify-end'>
          <InputCommon
            className='!max-w-[100px] h-6 rounded-lg text-center'
            placeholder='Số lượng'
            value={item.quantity || ''}
            onChange={handleQuantityChange}
            type='number'
            min={0}
            variantStatus='default'
            radius='lg'
            disabled={isCompleted}
            hardSize='xs'
          />
        </div>

        <div className='col-start-4'></div>
      </div>

      {dimensionLabels.length > 0 &&
        !isSession &&
        specs.map((spec) => (
          <div key={spec.id} className='grid grid-cols-[auto_minmax(0,1fr)_auto_auto]  gap-3 items-center'>
            <div className='grid grid-flow-col gap-1 auto-cols-min items-center'>
              {dimensionLabels.map((dimLabel, dimIndex) => (
                <React.Fragment key={`${spec.id}-dim-${dimIndex}`}>
                  {dimIndex > 0 && <span className='text-black text-xs'>X</span>}
                  <InputCommon
                    className='2xl:min-w-[60px] min-w-[50px]  text-center'
                    placeholder={dimLabel}
                    value={spec.dimensions[dimIndex] || ''}
                    disabled={isCompleted}
                    onChange={(e) => handleSpecDimensionChange(spec.id, dimIndex, e)}
                    type='decimal'
                    precision={2}
                    min={0}
                    variantStatus='default'
                    radius='lg'
                    hardSize='xs'
                    style={{
                      background: '#fff',
                      border: '1px solid rgba(0,0,0,0.05)',
                    }}
                  />
                </React.Fragment>
              ))}
            </div>
            {!isBetween1280and1350 && <div />}
            <div className='flex justify-end'>
              <div
                className={
                  'w-[100px] h-6 px-2 py-1 bg-white rounded-lg border border-black/5 flex items-center justify-center' +
                  (isCompleted ? ' bg-[var(--bgBlackOpacity)] cursor-not-allowed' : '')
                }
              >
                <div className='text-black text-sm'>{spec.quantity}</div>
              </div>
            </div>

            <button
              onClick={() => handleRemoveSpec(spec.id)}
              className={
                'w-3.5 h-3.5 flex items-center justify-center' + (isCompleted ? ' opacity-50 cursor-not-allowed' : '')
              }
              aria-label='Xóa quy cách'
              disabled={isCompleted}
            >
              <X size={14} className='text-rose-500' />
            </button>
          </div>
        ))}

      <div className='grid grid-cols-[auto_minmax(0,1fr)_auto_auto]  gap-3 items-center mt-2'>
        {dimensionLabels.length > 0 && !isSession && (
          <div className='justify-self-start'>
            <button
              onClick={handleAddSpec}
              className={
                'min-w-[140px] h-6 px-3 py-1 bg-blue-500 rounded-lg text-white text-xs flex items-center justify-center' +
                (isCompleted ? ' !bg-[var(--textBlack4)] cursor-not-allowed !text-black/60' : '')
              }
              disabled={isCompleted}
            >
              Thêm quy cách
            </button>
          </div>
        )}
        <div></div>
        <div className='text-right text-black/60 text-sm font-medium'>Tổng:</div>
        <div className='flex justify-end'>
          <div className='w-[100px] h-6 flex items-center justify-center'>
            <div className='text-[var(--sematicSuccessColor500)] text-xs font-bold'>
              {formatPrice(totalPrice)} {item.productVersion.currencyUnit?.name}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
